// Presenter/quote layer v2. JS twin of pipeline/present.py (the Python side is
// the oracle; parity enforced by recommend.test.js against present_fixtures.json).
// Consumes CQEngine.recommend + ENGINE_DATA.PRESENT_PRODUCTS.
(function (root) {
  "use strict";

  var CQE = root.CQEngine || (typeof require !== "undefined" ? require("./recommend.js") : null);

  var DEDICATED = [
    ["Turbidity/Sediment Filter", "turbidity_wh"],
    ["Lead Removal System", "lead_wh"],
    ["Arsenic Removal System", "arsenic_wh"],
    ["Nitrate Removal System", "nitrate_wh"],
    ["Fluoride Removal System", "fluoride_wh"],
    ["Uranium/Radium Removal", "radium_wh"],
    ["Radionuclide Removal System", "radium_wh"],
    ["Iron/Manganese/Sulfur Removal", "iron_wh"],
    ["Tannin Removal System", "tannin_wh"],
    ["Silica Treatment System", "silica_wh"],
    ["Sulfate Removal System", "sulfate_wh"],
    ["Strontium Removal System", "softener"],
    ["Water Softener", "softener"],
    ["Acid Neutralizer", "acid_wh"],
  ];
  // customer-facing wording for the "also flagged in your water" line: what
  // sits in the water is the contaminant, not the SYSTEM name we sell for it
  function companionFlagName(nm) {
    if (nm === "Acid Neutralizer") return "Acidic water";
    var sufs = [" Removal System", " Treatment System", " Removal", " System"];
    for (var i = 0; i < sufs.length; i++) {
      var s = sufs[i];
      if (nm.length > s.length && nm.slice(-s.length) === s) return nm.slice(0, -s.length);
    }
    return nm;
  }
  // Structured includes (Sol-converged 2026-07-29): {name, optional} rows so
  // the UI renders "What is included" without parsing display strings.
  var TANK_INCLUDES = [
    { name: "Pleated sediment pre-filter", optional: false },
    { name: "Carbon block post-filter", optional: false }
  ];

  function toMgl(v, u) {
    var f = { "ppb": .001, "ug/l": .001, "µg/l": .001, "ppt": 1e-6, "ng/l": 1e-6,
      "ppm": 1, "mg/l": 1, "g/l": 1000 };
    u = (u || "mg/L").trim().toLowerCase();
    return u in f ? v * f[u] : null;
  }
  function toGpg(v, u) {
    var f = { "gpg": 1, "mg/l": .0584, "ppm": .0584, "mmol/l": 5.847, "dgh": 1.04, "fh": .584 };
    u = (u || "gpg").trim().toLowerCase();
    return u in f ? v * f[u] : null;
  }

  var RADIO_FED2X = { "Gross Alpha": 30, "Gross Alpha (incl. U)": 30, "Strontium-90": 16 };
  var REGULATED_FIRST = { "PFOA": 1, "PFOS": 1, "PFHxS": 1, "PFNA": 1, "GenX": 1 };
  var HEALTH_HIGH_SYS = { nitrate: 1, fluoride: 1, radioactive: 1, barium: 1,
    selenium: 1, cadmium: 1, antimony: 1, beryllium: 1, thallium: 1, cyanide: 1 };

  function sevWeights(rec, data) {
    var name2cid = {};
    var ZT = data.ZERO_TOLERANCE || {};
    for (var cid in ZT) name2cid[ZT[cid].name] = cid;
    var name2sys = {};
    var TH = data.THRESHOLDS || {};
    for (var tc in TH) name2sys[TH[tc].name] = TH[tc].system;
    var ZTF = data.ZT_FLOORS || {};
    var w = {};
    rec.summary.forEach(function (s) {
      var st = s.status;
      var base = st === "high" ? 3 : st === "detected" ? 2 :
        (st === "elevated" || st === "low") ? 1 : 0;
      if (st === "detected") {
        var c = name2cid[s.name];
        var fl = c ? ZTF[c] : null;
        if (fl) {
          var conv = function (kind) {
            return kind === "activity"
              ? CQE.convertRadioactivity(s.value, s.unit || "", c)
              : toMgl(s.value, s.unit || "mg/L");
          };
          var cv = conv(fl.kind), ref = fl.v;
          if (cv === null && fl.alt_kind) { cv = conv(fl.alt_kind); ref = fl.alt_v; }
          if (cv !== null && cv >= 2 * ref) base = 10;
        }
      }
      if (st === "high" && RADIO_FED2X[s.name]) {
        var rv2 = CQE.convertRadioactivity(s.value, s.unit || "", null);
        if (rv2 !== null && rv2 >= RADIO_FED2X[s.name]) base = 10;
      }
      if (st === "high" && HEALTH_HIGH_SYS[name2sys[s.name]]) base = 10;
      if (s.name === "pH") base = Math.min(base, 1);
      if (!(s.name in w) || w[s.name] < base) w[s.name] = base;
    });
    return w;
  }

  function grade(weights) {
    var vals = [], k;
    for (k in weights) vals.push(weights[k]);
    var mx = 0, total = 0;
    vals.forEach(function (v) { if (v > mx) mx = v; total += v; });
    if (mx >= 10 || total >= 8) return "serious";
    var hasZt = false;
    vals.forEach(function (v) { if (v === 2) hasZt = true; });
    if (hasZt || total >= 4) return "moderate";
    return "baseline";
  }
  var SEV_ORDER = { baseline: 0, moderate: 1, serious: 2 };

  function numProfile(profile) {
    var out = { hardness_gpg: 0, tds: 0, iron: 0, hardness_seen: false };
    (profile || []).forEach(function (e) {
      var v = e.value, u = e.unit;
      if (e.canonical_id === "hardness") { out.hardness_seen = true; var h = toGpg(v, u); if (h) out.hardness_gpg = Math.max(out.hardness_gpg, h); }
      else if (e.canonical_id === "tds") { var m = toMgl(v, u); if (m) out.tds = Math.max(out.tds, m); }
      else if (e.canonical_id === "iron") { var m2 = toMgl(v, u); if (m2) out.iron = Math.max(out.iron, m2); }
    });
    return out;
  }

  function nameToCid(data) {
    var m = {}, TH = data.THRESHOLDS || {};
    for (var t in TH) m[TH[t].name] = t;
    var ZT = data.ZERO_TOLERANCE || {};
    for (var z in ZT) m[ZT[z].name] = z;
    return m;
  }

  function present(profile, context, data) {
    context = context || {};
    if (context.prefer_ro) {
      var pctx = {}; for (var pk in context) pctx[pk] = context[pk];
      pctx.force_ro = true; context = pctx;
    }
    data = data || root.ENGINE_DATA;
    var PP = data.PRESENT_PRODUCTS;
    var rec = CQE.recommend(profile, context, data);
    CQE.attachProducts(rec, data);
    var flags = rec.flags;
    var names = rec.train.map(function (s) { return s.name; });
    var vals = numProfile(profile);
    // v4 joint spec (AJ 2026-07-27): evidence provenance is first-class.
    // {canonical_id: "report"|"records"|"concern"|"symptom"}; absent = legacy.
    var prov = context.provenance || {};
    var allSensory = !!(profile && profile.length) && profile.every(function (e) {
      return prov[e.canonical_id] === "symptom";
    });
    var source = context.source || "city";
    var bacteria = !!(context.coliform || context.ecoli);
    (profile || []).forEach(function (e) {
      if (e.canonical_id === "ecoli") {
        var bv = parseFloat(e.value);
        if (!isNaN(bv) && bv > 0) bacteria = true;
      }
    });

    // AJ 07-21: sodium alone no longer forces whole-house RO; chloride, high
    // TDS, and the RO-preferred classes (trace metals, radionuclides,
    // specialty inorganics) still do.
    // AJ ruling 2026-07-29: a microplastics concern anchors reverse osmosis
    // (the main fix); SMART is the acceptable media alternative offered below.
    var mpIntent = !!flags.microplastics_intent;
    var roRequired = !!(flags.has_chloride || flags.highTds || context.prefer_ro ||
                        mpIntent || (flags.ro_preferred && !allSensory));
    var pfas = !!flags.pfasDetected;

    var mediaNames = names;
    var displayRec = rec;
    if (rec.useRo && !roRequired) {
      var mctx = {}; for (var k in context) mctx[k] = context[k]; mctx.force_ro = false;
      var mrec = CQE.recommend(profile, mctx, data);
      CQE.attachProducts(mrec, data);
      mediaNames = mrec.train.map(function (s) { return s.name; });
      displayRec = mrec;
    }
    var dedicated = DEDICATED.filter(function (d) { return mediaNames.indexOf(d[0]) > -1; });

    var weights = sevWeights(rec, data);

    var notes = [], addons = [], companions = [];
    if (flags.has_sodium && !flags.has_chloride && !roRequired) {
      notes.push("Sodium at this level is mainly a drinking-water concern: the under-sink reverse osmosis option handles it at the kitchen tap, and a whole-house RO is the comprehensive route if you want every tap covered.");
    }
    var choice = null, alternative = null, primary, includes, severity;
    var config = null;
    function card(key, why) { var c = {}; var src = PP[key]; for (var k in src) c[k] = src[k]; if (why) c.why = why; return c; }

    if (roRequired) {
      primary = card("wh_ro", (flags.has_chloride || flags.highTds)
        ? "Dissolved solids (TDS/sodium/chloride) can only be removed by reverse osmosis, media filtration cannot touch them."
        : (mpIntent && !flags.ro_preferred)
        ? "You asked about microplastics. Plastic particles are membrane work: reverse osmosis membranes are far finer than the particles, the surest reduction we sell, and the system is designed to reduce the rest of this profile with it."
        : "Metals and specialty contaminants like these are membrane work: no single filter bed takes that whole list down, so we go straight to whole-house RO instead of stacking half-measures.");
      if (flags.ro_preferred_radionuclide) {
        notes.push("Prefer a media-based route for the radionuclides on this record? The Radium and Uranium Whole House Water Filter is the dedicated alternative our specialists can spec against your report.");
      }
      includes = [
        { name: "Pleated sediment pre-filter", optional: false },
        { name: "Carbon block pre-filter", optional: false },
        { name: "SMART multistage pre-filter", optional: false },
        { name: "Reverse osmosis membranes", optional: false },
        { name: "Remineralizing post-filter", optional: true }
      ];
      if (vals.hardness_gpg > 15) companions.push(card("softener", "Needed first: hardness above 15 gpg would scale the RO membranes, so softening rides ahead of them."));
      else if (names.indexOf("Water Softener (RO Pre-Treatment)") > -1) companions.push(card("softener", "Softening ahead of the membranes protects them from scale."));
      if (names.indexOf("Silica Reduction System (RO Pre-Treatment)") > -1) companions.push(card("silica_wh", "Silica fouls RO membranes, treat it before the RO."));
      if (names.indexOf("Iron/Metal Removal System (RO Pre-Treatment)") > -1) companions.push(card("iron_wh", "Iron/manganese must come out before the membranes."));
      if (bacteria) companions.push(card("uv", "Bacteria risk: UV after the RO protects the stored water - the catalog calls for UV on microbially unsafe wells."));
      if (context._ro_driver) {
        primary.why = "The biggest issue on this record is " + context._ro_driver + ", which has no dedicated residential tank. Whole-house reverse osmosis is the dependable path for it and is designed to reduce the rest of the profile with it.";
      } else if (context._consolidated) {
        primary.why = "This record calls for several different treatment chemistries at once. One whole-house reverse osmosis system is designed to reduce the entire profile in a single system instead of a row of separate filter tanks." + (companions.length ? " Required pre-treatment is included below." : "");
      } else if (context.prefer_ro && !flags.hasRoOnly && !flags.highTds) {
        primary.why = "Maximum purity by preference: reverse osmosis at every tap, the broadest reduction in one system.";
      }
      var preSt = null;
      rec.train.forEach(function (s) { if (s.name === "Iron/Metal Removal System (RO Pre-Treatment)") preSt = s; });
      if (preSt) {
        var preRs = [], preSeen = {};
        (preSt.reasons || []).forEach(function (r) {
          if (r.contaminant !== "City Water" && r.contaminant !== "All Water" && r.contaminant !== "Final Polish" && !preSeen[r.contaminant]) {
            preSeen[r.contaminant] = 1; preRs.push(r.contaminant);
          }
        });
        if (preRs.length) {
          companions.forEach(function (c) {
            if (c.handle === PP.iron_wh.handle) c.why = preRs.join(", ") + " must come out before the membranes.";
          });
        }
      }
      var w10 = [];
      for (var wn in weights) {
        if (weights[wn] >= 10 && wn !== context._ro_driver) w10.push(wn);
      }
      if (w10.length) {
        var p10 = w10.length > 1;
        notes.push(w10.slice(0, 4).join(", ") + (p10 ? " are" : " is") + " above the federal standard on this record; the membranes are designed to reduce " + (p10 ? "these" : "it") + " as well.");
      }
      notes.push("Whole-house RO needs a storage tank kit (165-500 gal, with pump) sized to your home.");
      // AJ 2026-07-29: microplastics is the one RO-anchored driver with an
      // acceptable media route - offer SMART as the alternative when nothing
      // else on the profile forces the membranes.
      var mpSole = mpIntent && !flags.has_chloride && !flags.highTds &&
        !flags.ro_preferred && !pfas && flags.specialtyCount === 0 && !context.prefer_ro;
      if (mpSole) {
        alternative = card("smart_wh", "Filter-bed route: SMART's multimedia bed also reduces microplastics at every tap. The reverse osmosis pick above is the stronger reduction.");
      }
      if (flags.hasRoOnly || flags.highTds || context._consolidated) {
        severity = SEV_ORDER[grade(weights)] >= 2 ? "serious" : "moderate";
      } else {
        severity = grade(weights);
      }
    } else if (dedicated.length) {
      var FILLER = { "City Water": 1, "All Water": 1, "Final Polish": 1 };
      function stageScore(stageName) {
        var st = null;
        displayRec.train.forEach(function (s) { if (s.name === stageName) st = s; });
        if (!st) return 0;
        var seenR = {}, tot = 0;
        (st.reasons || []).forEach(function (r) {
          var c = r.contaminant;
          if (FILLER[c] || seenR[c]) return;
          seenR[c] = 1; tot += (weights[c] || 0);
        });
        return tot;
      }
      var keyScores = {}, keyOrder = {};
      dedicated.forEach(function (d, i) {
        keyScores[d[1]] = (keyScores[d[1]] || 0) + stageScore(d[0]);
        if (!(d[1] in keyOrder)) keyOrder[d[1]] = i;
      });
      var smartScore = stageScore("SMART Whole House Filter");
      // AJ 2026-07-27 (v4 joint spec): softening never anchors while any
      // filtration driver exists - hardness rides as the anchor's
      // configuration instead. Softening still anchors a softening-only
      // profile (hardness/strontium with nothing else to filter).
      var SOFT_KEYS = { softener: 1 };
      var radonStage = displayRec.train.some(function (st) { return st.name === "Radon Reduction (GAC)"; });
      var disinfectStage = displayRec.train.some(function (st) { return st.name === "UV Disinfection System" || st.name === "Chlorine Injection System"; });
      var wellchlorStage = displayRec.train.some(function (st) {
        return (st.reasons || []).some(function (r) { return r.contaminant === "Well Chlorination"; });
      });
      var hasFiltrationDriver = smartScore > 0 || radonStage || disinfectStage || wellchlorStage;
      // (config guard below also excludes a saltfree anchor re-emitting itself)
      for (var fk in keyScores) { if (!SOFT_KEYS[fk]) hasFiltrationDriver = true; }
      var eligible = {};
      for (var ek in keyScores) {
        if (!hasFiltrationDriver || !SOFT_KEYS[ek]) eligible[ek] = keyScores[ek];
      }
      var bestScore = -1, bestKey = null;
      for (var kk in eligible) {
        if (eligible[kk] > bestScore ||
            (eligible[kk] === bestScore && keyOrder[kk] < keyOrder[bestKey])) {
          bestScore = eligible[kk]; bestKey = kk;
        }
      }
      var KNOWN = { "Sediment Pre-Filter": 1, "SMART Whole House Filter": 1,
        "Carbon Post-Filter": 1, "Built-In Prefilters (Sediment + Carbon Block + SMART)": 1,
        "Whole House Reverse Osmosis": 1, "Alkalizing Remineralization Filter": 1,
        "UV Disinfection System": 1, "Water Softener (RO Pre-Treatment)": 1,
        "Silica Reduction System (RO Pre-Treatment)": 1,
        "Iron/Metal Removal System (RO Pre-Treatment)": 1 };
      DEDICATED.forEach(function (d) { KNOWN[d[0]] = 1; });
      var unmappedBest = 0, unmappedName = null;
      displayRec.train.forEach(function (st2) {
        if (KNOWN[st2.name]) return;
        (st2.reasons || []).forEach(function (r2) {
          var wv = weights[r2.contaminant] || 0;
          if (!FILLER[r2.contaminant] && wv > unmappedBest) {
            unmappedBest = wv; unmappedName = r2.contaminant;
          }
        });
      });
      var n2c = nameToCid(data);
      var uCid = unmappedName ? n2c[unmappedName] : null;
      var sensoryDriver = !!uCid && prov[uCid] === "symptom";
      if (unmappedBest >= 3 && unmappedBest > bestScore &&
          unmappedBest > smartScore && !sensoryDriver && !context.prefer_ro) {
        var uctx = {}; for (var uk in context) uctx[uk] = context[uk];
        uctx.prefer_ro = true; uctx._consolidated = true; uctx._ro_driver = unmappedName;
        return present(profile, uctx, data);
      }
      if (sensoryDriver && unmappedBest >= 3) {
        notes.push("What you described can suggest " + unmappedName +
          ", but a symptom is not a measurement. This setup is sized around" +
          " what is confirmed; a simple test settles " + unmappedName +
          " before you invest in dedicated treatment for it.");
      }
      var primKey;
      if (smartScore > bestScore) {
        var sst = null;
        displayRec.train.forEach(function (s) { if (s.name === "SMART Whole House Filter") sst = s; });
        var tops = [];
        ((sst && sst.reasons) || []).forEach(function (r) {
          if (!FILLER[r.contaminant] && r.contaminant !== "Well Chlorination" && tops.indexOf(r.contaminant) < 0) tops.push(r.contaminant);
        });
        tops.sort(function (a, b) {
          var d = (weights[b] || 0) - (weights[a] || 0);
          if (d) return d;
          return (REGULATED_FIRST[b] ? 1 : 0) - (REGULATED_FIRST[a] ? 1 : 0);
        });
        primKey = "smart_wh";
        var smartWhy;
        if (tops.length) {
          smartWhy = "The main load to design for here is " + tops.slice(0, 3).join(", ") + ". SMART's multimedia bed treats that class at every tap, so it anchors the setup.";
        } else if (radonStage) {
          smartWhy = "Radon reduces with granular activated carbon contact at the point of entry, and the SMART multimedia bed carries exactly that GAC class house-wide. Confirm the level with a radon water test first.";
        } else if (wellchlorStage) {
          smartWhy = "Your well runs a chlorinator, and chlorinated water needs carbon contact after disinfection. The SMART multimedia bed clears the chlorine and its byproducts at every tap, so it anchors the setup.";
        } else if (disinfectStage) {
          smartWhy = "Bacteria risk leads this design. Disinfection works most reliably after proper filtration, so the multimedia bed anchors the setup with UV riding after it. The Eagle build covers bacteria with its built-in UF membrane if you would rather skip a separate UV.";
        } else {
          smartWhy = "This record carries flags beyond everyday chlorine and taste. A multimedia whole-house filter anchors the setup; the notes below cover the specifics.";
        }
        primary = card("smart_wh", smartWhy);
      } else {
        primKey = bestKey;
        var anchorReasons = [];
        dedicated.forEach(function (d) {
          if (d[1] !== bestKey) return;
          var st3 = null;
          displayRec.train.forEach(function (t) { if (t.name === d[0]) st3 = t; });
          ((st3 && st3.reasons) || []).forEach(function (r) {
            var c = r.contaminant;
            if (!FILLER[c] && c !== "Well Chlorination" && anchorReasons.indexOf(c) < 0) anchorReasons.push(c);
          });
        });
        primary = card(bestKey, anchorReasons.length
          ? "The dedicated Crystal Quest system for " + anchorReasons.slice(0, 3).join(", ") + ", handled at every tap without needing reverse osmosis."
          : "The dedicated Crystal Quest system for your primary issue, no reverse osmosis required.");
        primKey = bestKey;
        // AJ 2026-07-27: softening-only + UNMEASURED (symptom) hardness anchors
        // the salt-free conditioner; measured >= 7 gpg keeps the true
        // softener. AJ 2026-07-29: an explicit concern on CITY water also
        // anchors the anti-scale (city hardness is usually moderate; the
        // toggle still swaps); a well concern keeps the softener.
        var hpAnchor = prov.hardness;
        if (primKey === "softener" && vals.hardness_seen &&
            (hpAnchor === "symptom" || (hpAnchor === "concern" && source === "city"))) {
          primKey = "saltfree";
          primary = card("saltfree", hpAnchor === "concern"
            ? "You flagged hard water on city supply, where hardness is usually moderate: the salt-free anti-scale protects the whole house with zero upkeep, and the true softener swaps in if you want the soft-water feel."
            : "Scale signs without a measured hardness number: salt-free conditioning protects the whole house with zero salt, and a simple hardness test can upgrade this to a full softener later.");
        }
      }
      // AJ 2026-07-27 (good/better/best): SMART-anchored quotes always offer
      // the same-class Eagle upgrade pair; Guardian stays clean-city only.
      if (primKey === "smart_wh") {
        choice = (source === "city" && grade(weights) === "baseline")
          ? [card("smart_wh"), card("eagle_wh"), card("guardian_wh")]
          : [card("smart_wh"), card("eagle_wh")];
      }
      // AJ 2026-07-27: hardness rides as the anchor's configuration choice
      // (salt-free when unmeasured, salt softener on explicit concern or
      // measured hardness >= 7 gpg from a report/records).
      if (vals.hardness_seen && primKey !== "softener" && primKey !== "saltfree") {
        var hp = prov.hardness;
        // AJ ruling 2026-07-29: city + explicit concern defaults anti-scale;
        // well concern or measured >= 7 gpg keeps the true softener.
        if ((hp === "concern" && source !== "city") ||
            ((hp === "report" || hp === "records") && vals.hardness_gpg >= 7)) {
          config = card("softener");
          config.kind = "softener";
          config.why = "The hardness on this profile calls for true softening." +
            " The anchor system is available in a With Water Softener" +
            " configuration, which costs less than buying the two separately.";
        } else if (hp === "concern") {
          config = card("saltfree");
          config.kind = "conditioner";
          config.why = "You flagged hard water on city supply, where hardness" +
            " is usually moderate: the salt-free anti-scale configuration" +
            " protects pipes and appliances with zero upkeep, and the true" +
            " softener swaps in if you want the soft-water feel.";
        } else {
          config = card("saltfree");
          config.kind = "conditioner";
          config.why = "Scale signs without a measured hardness number: the" +
            " salt-free conditioner configuration protects pipes and appliances" +
            " without salt, and a simple hardness test can upgrade this to a" +
            " full softener later.";
        }
      }
      includes = TANK_INCLUDES.slice();
      var seenKeys = {}; seenKeys[primKey] = 1;
      // the salt-free anchor already owns the hardness load (AJ 07-29 city
      // anti-scale default): never quote the softener as a second companion
      // stage in the same build
      if (primKey === "saltfree") seenKeys.softener = 1;
      dedicated.forEach(function (d) {
        if (seenKeys[d[1]]) return;
        if (config && d[0] === "Water Softener") return;
        seenKeys[d[1]] = 1;
        var why = d[0] === "Strontium Removal System"
          ? "Strontium behaves like hardness: cation-exchange softening captures it along with calcium and magnesium."
          : companionFlagName(d[0]) + " also flagged in your water.";
        companions.push(card(d[1], why));
      });
      var smartStage = null;
      if (primKey !== "smart_wh") {
        displayRec.train.forEach(function (s) { if (s.name === "SMART Whole House Filter") smartStage = s; });
      }
      if (smartStage) {
        var seenR = {}, real = [];
        (smartStage.reasons || []).forEach(function (r) {
          if (!FILLER[r.contaminant] && r.contaminant !== "Well Chlorination" && !seenR[r.contaminant]) { seenR[r.contaminant] = 1; real.push(r.contaminant); }
        });
        if (!real.length && wellchlorStage) {
          companions.push(card("smart_wh", "Your chlorinator needs carbon after it: the SMART bed clears the chlorine and its byproducts at every tap."));
        }
        if (real.length) {
          var shown = real.slice(0, 3).join(", ");
          if (real.length > 3) shown += " and " + (real.length - 3) + " more";
          companions.push(card("smart_wh", "Also flagged in this water: " + shown + ". The SMART multimedia bed treats the chlorine, disinfection byproduct and VOC class house-wide."));
        }
      }
      if (pfas || flags.specialtyCount >= 2) {
        alternative = card("wh_ro", "Prefer maximum purity in one system? Whole-house RO covers all of this at once (RO systems send a reject stream to drain).");
      }
      severity = grade(weights);
      var TANKS = {};
      ["smart_wh","eagle_wh","guardian_wh","lead_wh","arsenic_wh","fluoride_wh",
       "nitrate_wh","iron_wh","tannin_wh","silica_wh","radium_wh","sulfate_wh",
       "acid_wh","softener","highflow"].forEach(function (k) { TANKS[PP[k].handle] = 1; });
      var nTanks = (TANKS[primary.handle] ? 1 : 0);
      companions.forEach(function (c) { if (TANKS[c.handle]) nTanks++; });
      if (nTanks >= 4 && !context.prefer_ro) {
        var cctx = {}; for (var ck in context) cctx[ck] = context[ck];
        cctx.prefer_ro = true; cctx._consolidated = true;
        return present(profile, cctx, data);
      }
    } else {
      primary = card("smart_wh", source === "city"
        ? "City water carries a predictable load: chlorine, the byproducts disinfection leaves behind, and the taste that comes with them. One multimedia whole-house filter is built for exactly that."
        : (context.continuous_chlorination
          ? "Your well runs a chlorinator, and chlorinated water needs carbon contact after disinfection. The SMART multimedia bed clears the chlorine and its byproducts at every tap, so it anchors the setup."
          // bacteria concern on an untested well: the why must name the risk
          // the UV stage answers, never read as "nothing reported" while UV
          // rides the build
          : context.coliform
          ? "Your answers raise a bacteria question, so disinfection is built into this line: the multimedia bed keeps the water clear ahead of the UV stage, and the UV then sterilizes every tap in the house."
          : "Nothing specific reported on this well, so we start where every good well setup starts: one broad multimedia bed that covers the common well-water loads while testing fills in the rest."));
      choice = source === "city" ? [card("smart_wh"), card("eagle_wh"), card("guardian_wh")] : null;
      includes = TANK_INCLUDES.slice();
      severity = "baseline";
      if (pfas) {
        primary.why = "PFAS detected: SMART's enhanced media reduces many PFAS-class chemicals house-wide; the reverse osmosis option is the maximum-purity path for the full PFAS list.";
        choice = null;
        alternative = card("wh_ro", "Maximum-purity option for PFAS: whole-house reverse osmosis.");
        severity = "moderate";
      } else if (flags.vocDetected) {
        primary.why = "Organic chemicals (solvents or pesticides) detected: SMART's multimedia bed adsorbs VOC-class organics house-wide.";
        choice = null;
        alternative = card("wh_ro", "Maximum-purity option for organic chemicals: whole-house reverse osmosis.");
        severity = "moderate";
      }
      var g = grade(weights);
      if (SEV_ORDER[g] > SEV_ORDER[severity]) severity = g;
      if (g !== "baseline") {
        choice = null;
        if (primary.why.indexOf("City water carries") === 0 || primary.why.indexOf("Nothing specific reported") === 0) {
          var bst = null;
          displayRec.train.forEach(function (s) { if (s.name === "SMART Whole House Filter") bst = s; });
          var btops = [];
          ((bst && bst.reasons) || []).forEach(function (r) {
            var c = r.contaminant;
            if (c !== "City Water" && c !== "All Water" && c !== "Final Polish" && btops.indexOf(c) < 0) btops.push(c);
          });
          btops.sort(function (a, b) {
            var d = (weights[b] || 0) - (weights[a] || 0);
            if (d) return d;
            return (REGULATED_FIRST[b] ? 1 : 0) - (REGULATED_FIRST[a] ? 1 : 0);
          });
          if (btops.length && (weights[btops[0]] || 0) > 0) {
            primary.why = "The main load to design for here is " + btops.slice(0, 3).join(", ") + ". SMART's multimedia bed treats that class at every tap, so it anchors the setup.";
          } else {
            primary.why = "This record carries flags beyond everyday chlorine and taste. A multimedia whole-house filter anchors the setup; the notes below cover the specifics.";
          }
        }
      }
      if (choice) notes.push("Guardian is the budget pick: a compact three-stage build for straightforward city water.");
      if (choice === null) {
        choice = [card("smart_wh"), card("eagle_wh")];
      }
    }

    // 2026-08-02: a salt-free ANCHOR is itself the hardness treatment, so it
    // excludes this lifestyle card the same way the softener anchor does
    if (config === null && vals.hardness_gpg > 3 && vals.hardness_gpg <= 15 &&
        primary.handle !== PP.softener.handle && primary.handle !== PP.wh_ro.handle &&
        primary.handle !== PP.saltfree.handle) {
      var already = companions.some(function (c) { return c.handle === PP.softener.handle; });
      if (!already) companions.push(card("softener",
        "Hardness at " + Math.round(vals.hardness_gpg) + " gpg, bundle a softener (or salt-free conditioner if you want zero salt and to keep minerals)."));
    }

    // clean city water = general filtration (AJ 07-16): suggest scale
    // protection even when hardness is not on the record; stay quiet when the
    // record shows genuinely soft water
    if (source === "city" && severity === "baseline" && !vals.hardness_seen &&
        primary.handle !== PP.softener.handle && primary.handle !== PP.wh_ro.handle &&
        !companions.some(function (c) {
          return c.handle === PP.softener.handle || c.handle === PP.saltfree.handle;
        })) {
      companions.push(card("saltfree",
        "Hardness is not on this record, but most city water carries enough to scale water heaters and fixtures over time. Salt-free conditioning protects the whole house with zero salt (a traditional softener works too)."));
    }

    var MAPPED_STAGES = { "Sediment Pre-Filter": 1, "SMART Whole House Filter": 1,
      "Carbon Post-Filter": 1, "Built-In Prefilters (Sediment + Carbon Block + SMART)": 1,
      "Whole House Reverse Osmosis": 1, "Alkalizing Remineralization Filter": 1,
      "UV Disinfection System": 1, "Chlorine Injection System": 1,
      "Water Softener (RO Pre-Treatment)": 1,
      "Silica Reduction System (RO Pre-Treatment)": 1,
      "Iron/Metal Removal System (RO Pre-Treatment)": 1 };
    DEDICATED.forEach(function (d) { MAPPED_STAGES[d[0]] = 1; });
    var GFILLER = { "City Water": 1, "All Water": 1, "Final Polish": 1 };
    var roPrimary = primary.handle === PP.wh_ro.handle;
    var unmapped = [];
    displayRec.train.forEach(function (st) {
      if (MAPPED_STAGES[st.name]) return;
      (st.reasons || []).forEach(function (r) {
        var c = r.contaminant;
        if (!GFILLER[c] && unmapped.indexOf(c) < 0) unmapped.push(c);
      });
    });
    var hasRadonStage = displayRec.train.some(function (st) { return st.name === "Radon Reduction (GAC)"; });
    if (hasRadonStage) {
      notes.push("Radon reduces with granular activated carbon contact at the point of entry: the SMART, Guardian, and Eagle whole-house systems all carry GAC beds, stepping up in breadth from budget to broadest reduction. Confirm the level with a radon water test first.");
      unmapped = unmapped.filter(function (c) { return c.toLowerCase().indexOf("radon") < 0; });
    }
    if (unmapped.length && !roPrimary) {
      notes.push(unmapped.slice(0, 4).join(", ") + " on this record: no dedicated residential tank targets " + (unmapped.length > 1 ? "these" : "this") + ". The whole-house reverse osmosis option is the dependable path.");
    }

    var phRow = null;
    rec.summary.forEach(function (s) { if (!phRow && s.name === "pH" && s.status === "high") phRow = s; });
    if (phRow) {
      if (roPrimary) {
        notes.push("pH reads high at " + phRow.value + ". High pH is a scale and taste factor more than a health risk; the reverse osmosis system recommended above lowers it at the tap.");
      } else {
        notes.push("pH reads high at " + phRow.value + ". High pH is a scale and taste factor more than a health risk; conditioning helps manage its effects, and the reverse osmosis option lowers it at the tap.");
      }
    }

    var TRACE_FAMILY = { "Lead": "lead_wh", "Arsenic": "arsenic_wh",
      "Uranium": "radium_wh", "Uranium-238": "radium_wh",
      "Radium (combined)": "radium_wh", "Radium-226": "radium_wh",
      "Radium-228": "radium_wh" };
    var quotedH = {}; quotedH[primary.handle] = 1;
    companions.forEach(function (c) { quotedH[c.handle] = 1; });
    var pfasNames = {};
    var ZTall = data.ZERO_TOLERANCE || {};
    for (var zc in ZTall) { if (ZTall[zc].system === "pfas") pfasNames[ZTall[zc].name] = 1; }
    var pfasQuoted = pfas && (primary.handle === PP.smart_wh.handle || primary.handle === PP.wh_ro.handle);
    var traceItems = (rec.trace || []).filter(function (t) {
      var fk = TRACE_FAMILY[t.contaminant];
      if (fk && quotedH[PP[fk].handle]) return false;
      if (pfasQuoted && pfasNames[t.contaminant]) return false;
      return true;
    });
    if (traceItems.length) {
      var tn = traceItems.slice(0, 4).map(function (t) {
        return t.contaminant + " (" + t.value + " " + (t.unit || "") + ")";
      }).join(", ");
      var more = traceItems.length - 4;
      if (more > 0) tn += " and " + more + " more";
      if (primary.handle === PP.wh_ro.handle) {
        notes.push("Trace detections on this record (" + tn + ") sit below Crystal Quest action levels, and the whole-house reverse osmosis recommended above is already designed to reduce every one of them.");
      } else {
        notes.push("Trace detections on this record (" + tn + ") sit below Crystal Quest action levels, so dedicated removal systems are not urgently needed. If you want extra margin on them anyway, the reverse osmosis option is designed to reduce this entire list in one system.");
      }
    }

    if (alternative === null && primary.handle !== PP.wh_ro.handle) {
      alternative = card("wh_ro", "Want maximum purity in one move? Whole-house reverse osmosis lowers dissolved solids and covers the broadest range of contaminants at every tap.");
    }

    if (flags.tdsMgl > 300 && flags.tdsMgl <= 500 && !roRequired) {
      notes.push("TDS is moderately elevated (" + Math.round(flags.tdsMgl) + " mg/L). Only reverse osmosis lowers dissolved solids - an option if you want that number down.");
    }

    // AJ ruling 2026-08-03: the chlorine dosing system earns a quoted stage
    // on well-class builds: it disinfects the bacteria risk AND oxidizes
    // iron/manganese/sulfur ahead of the filters; downstream carbon strips
    // the residual. Doses FIRST (rank 5), never the anchor. Suppressed when
    // the well already runs a chlorinator (that equipment IS this stage).
    var chlorStage = displayRec.train.some(function (st) { return st.name === "Chlorine Injection System"; });
    var metalSt = null;
    displayRec.train.forEach(function (st) { if (!metalSt && st.name === "Iron/Manganese/Sulfur Removal") metalSt = st; });
    var metalStage = !!metalSt;
    // AJ 2026-08-03 (round 2): a HEAVY metals load alone earns the dosing
    // stage on a well - multiple distinct metal signals (iron plus manganese,
    // sulfur odor) or measured iron at 3 mg/L and up. A single mild iron
    // signal stays with the metal filter alone.
    var DOSE_FILLER = { "City Water": 1, "All Water": 1, "Final Polish": 1 };
    var metalSignals = {};
    if (metalSt) (metalSt.reasons || []).forEach(function (r) { if (!DOSE_FILLER[r.contaminant]) metalSignals[r.contaminant] = 1; });
    var heavyMetals = source === "well" && (Object.keys(metalSignals).length >= 2 || vals.iron >= 3);
    var dosingWhy = null;
    if ((chlorStage || (bacteria && source === "well" && metalStage)) && !context.continuous_chlorination) {
      dosingWhy = metalStage
        ? "Chlorine dosing leads this line: one metered stage handles the bacteria risk and oxidizes the iron, manganese and sulfur load so the filters behind it work at full strength. The carbon stage downstream strips the residual before your taps."
        : "Chlorine dosing leads this line with metered residual disinfection ahead of the filters. The carbon stage downstream strips the residual before your taps.";
    } else if (heavyMetals && !context.continuous_chlorination) {
      dosingWhy = "Metered oxidation ahead of the metal filter: at this load, pre-oxidizing lets the iron, manganese and sulfur stage strip cleanly instead of fouling early. The carbon stage downstream strips the residual before your taps.";
    } else if (vals.iron > 5) {
      dosingWhy = "Iron above 5 mg/L needs oxidation pre-treatment ahead of any filter.";
    }
    if (dosingWhy && primary.handle !== PP.oxiorg.handle) {
      addons.push(card("oxiorg", dosingWhy));
      // AJ catch 2026-08-03: chlorine dosing promises downstream carbon -
      // if no carbon bed rides the build, the SMART tank joins (same canon
      // as chlorinated wells: carbon contact after the chlorinator, always)
      var carbonHandles = {};
      ["smart_wh","eagle_wh","guardian_wh","wh_ro"].forEach(function(k){ carbonHandles[PP[k].handle]=1; });
      var haveCarbon = carbonHandles[primary.handle] ||
        companions.some(function(c){ return carbonHandles[c.handle]; });
      if (!haveCarbon) {
        companions.push(card("smart_wh", "Chlorine dosing needs carbon behind it: the SMART multimedia bed clears the residual chlorine and disinfection byproducts before your taps."));
      }
    }
    // AJ 2026-08-03: every whole-house RO build carries OPTIONAL
    // remineralization after the membrane (removable in the build editor)
    if (primary.handle === PP.wh_ro.handle) {
      addons.push(card("remin_wh", "Optional remineralization: reverse osmosis strips minerals along with contaminants; this stage adds balanced minerals back and lifts pH after the membrane."));
    }
    var eaglePrimary = primary.handle === PP.eagle_wh.handle;
    if (bacteria && !eaglePrimary && primary.handle !== PP.wh_ro.handle) {
      addons.push(card("uv", "Bacteria risk: UV disinfection sized to your flow, installed after filtration."));
    }
    if (bacteria && choice) notes.push("Eagle includes a 0.2-micron UF membrane, choosing Eagle covers bacteria without a separate UV.");

    // oracle parity: the sodium severity bump lands BEFORE the pou choice
    var sodiumNote = !!(flags.has_sodium && !flags.has_chloride && !roRequired);
    if (sodiumNote && severity === "baseline") severity = "moderate";
    var pou;
    if (bacteria) pou = card("thunder_uv", "Drinking-water-only option: under-sink RO finished with UV.");
    else if (severity === "baseline") pou = card("thunder", "Just want better drinking water? An under-sink RO delivers it for a fraction of a whole-house system.");
    else {
      pou = card("thunder", "Budget path: treat the water you drink and cook with first; add whole-house later.");
      var cart = dedicated.filter(function (d) {
        return d[1] === "arsenic_wh" || d[1] === "fluoride_wh" || d[1] === "nitrate_wh";
      });
      if (cart.length) {
        pou.why += " Ask for the " + cart[0][0].replace(" Removal System", "").toLowerCase() +
          " cartridge configuration - the base unit needs the dedicated cartridge for that contaminant.";
      }
    }
    // AJ 2026-07-29: when microplastics drives the design, the drinking-water
    // option names it (the product is already under-sink RO on these paths).
    if (mpIntent && !bacteria && pou.handle === PP.thunder.handle) {
      pou.why = "Microplastics at the kitchen tap: under-sink reverse osmosis is the surest reduction for the water you drink and cook with.";
    }
    // AJ 2026-08-03: on ordinary water the SMART under-sink rides along as
    // the membrane-free drinking-water alternative; it never replaces RO
    // where a membrane earns the slot (bacteria, microplastics, cartridges).
    var pouAlt = null;
    if (severity === "baseline" && !bacteria && !mpIntent) {
      pouAlt = card("smart_us", "Prefer filtration without a membrane or drain line? The SMART under-sink polishes taste and everyday chemistry at the tap.");
    }

    var baths = String(context.baths || "");
    var size;
    // live HF tiers (2026-08-03): 15 GPM = 1-3 baths, 20 = 4+, 35 = 6+
    if (baths === "5+") size = { tank: "High Flow, 20 GPM tier (15 and 35 GPM also available)", upgrade: PP.highflow };
    else if (baths === "3-4") size = { tank: "2.0 cu ft (10-13 GPM)" };
    else size = { tank: "1.5 cu ft (9-11 GPM)" };
    // WH-RO sizes in GPD + storage; always computed so the RO alternative
    // card and crossover can size the system honestly (AJ 2026-08-03)
    // AJ 2026-08-03: an EXACT default size per household (live variant
    // tiers), not a range; every axis stays changeable on the product page
    var roSize = baths === "5+" ? "4000 GPD + 500 gal storage"
               : baths === "3-4" ? "1500 GPD + 220 gal storage"
               : "500 GPD + 165 gal storage";
    if (primary.handle === PP.wh_ro.handle) size = { ro: roSize };
    else size.ro_alt = roSize;
    if (!baths && !context.people) size.assumed = true;


    return { severity: severity, consolidated: !!context._consolidated,
      primary: primary, choice: choice, config: config, companions: companions,
      alternative: alternative, pou: pou, pou_alt: pouAlt, includes: includes, addons: addons,
      sizing: size, notes: notes, engine: displayRec, ideal: (function () {
        var RANK_KEYS = { oxiorg: 5, turbidity_wh: 8, iron_wh: 10, softener: 20, acid_wh: 25, silica_wh: 30, smart_wh: 40, wh_ro: 60, remin_wh: 70, uv: 90 };
        var rank = {};
        Object.keys(PP).forEach(function (k) { rank[PP[k].handle] = RANK_KEYS.hasOwnProperty(k) ? RANK_KEYS[k] : 50; });
        var pool = [primary], seen = {}; seen[primary.handle] = 1;
        companions.concat(addons).forEach(function (c) {
          if (!seen[c.handle]) { seen[c.handle] = 1; pool.push(c); }
        });
        // v4 joint spec (AJ 2026-07-27): POU polish is an explicit CHOICE
        // offered via res.pou, never auto-baked into the ideal bundle.
        var items = pool.map(function (it, i) { return { it: it, i: i }; })
          .sort(function (a, b) { return (rank[a.it.handle] || 50) - (rank[b.it.handle] || 50) || a.i - b.i; })
          .map(function (x) { return x.it; });
        return { title: "The complete Crystal Quest setup", items: items,
          note: "Whole-house protection at the point of entry, each stage sequenced to protect the ones after it. Add the under-sink reverse-osmosis polish at the kitchen tap if you want bottled-quality drinking water on top." };
      })() };
  }

  var api = { present: present };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  else root.CQPresent = api;
})(typeof window !== "undefined" ? window : this);
